import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ServiceBase } from 'app/core/services/service.base';
import {
  Campaign,
  CampaignStatus,
  CreateCampaignDto,
  UpdateCampaignDto,
} from 'app/shared/models/campaign';

const CAMPAIGNS_ENDPOINT = 'campaigns';
const SEARCH_PARAM = 'search';
const IMAGE_FORM_FIELD_NAME = 'image';

/**
 * Campaigns published to the isoFarm support portal.
 *
 * @remarks
 * Every endpoint is admin-only on the backend, so callers must gate their calls
 * behind `USER_ADMINISTRATION` + `CREATE` — otherwise the request answers 403 and
 * the global error interceptor surfaces a toast to a user who cannot act on it.
 */
@Injectable({ providedIn: 'root' })
export class CampaignsService extends ServiceBase {
  private readonly http = inject(HttpClient);

  private readonly _campaigns = new BehaviorSubject<Campaign[]>([]);

  readonly campaigns$ = this._campaigns.asObservable();

  /** Emits the number of registered campaigns, for the support page counter. */
  readonly campaignsCount$ = this.campaigns$.pipe(
    map((campaigns) => campaigns.length),
  );

  /**
   * Lists campaigns, optionally filtered by title.
   *
   * @param search - Case-insensitive substring matched against the title.
   */
  list(search?: string): Observable<Campaign[]> {
    let params = new HttpParams();

    if (search) {
      params = params.set(SEARCH_PARAM, search.trim());
    }

    return this.http
      .get<Campaign[]>(`${this.UrlServiceV1}${CAMPAIGNS_ENDPOINT}`, {
        params,
        ...this.obterAuthHeader(),
      })
      .pipe(
        tap((campaigns) => this._campaigns.next(campaigns)),
        catchError((error) => this.handleError(error)),
      );
  }

  getById(id: string): Observable<Campaign> {
    return this.http
      .get<Campaign>(
        `${this.UrlServiceV1}${CAMPAIGNS_ENDPOINT}/${id}`,
        this.obterAuthHeader(),
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  create(dto: CreateCampaignDto): Observable<Campaign> {
    return this.http
      .post<Campaign>(
        `${this.UrlServiceV1}${CAMPAIGNS_ENDPOINT}`,
        {
          title: dto.title,
          description: dto.description,
          status: dto.status,
          url: dto.url ?? null,
          announceOnLogin: dto.announceOnLogin === true,
        },
        this.obterAuthHeader(),
      )
      .pipe(
        tap((campaign) => this.prependToState(campaign)),
        catchError((error) => this.handleError(error)),
      );
  }

  update(id: string, dto: UpdateCampaignDto): Observable<Campaign> {
    return this.http
      .put<Campaign>(
        `${this.UrlServiceV1}${CAMPAIGNS_ENDPOINT}/${id}`,
        {
          title: dto.title,
          description: dto.description,
          status: dto.status,
          url: dto.url ?? null,
          announceOnLogin: dto.announceOnLogin === true,
          imageUrl: dto.imageUrl,
        },
        this.obterAuthHeader(),
      )
      .pipe(
        tap((campaign) => this.replaceInState(campaign)),
        catchError((error) => this.handleError(error)),
      );
  }

  delete(id: string): Observable<void> {
    return this.http
      .delete<void>(
        `${this.UrlServiceV1}${CAMPAIGNS_ENDPOINT}/${id}`,
        this.obterAuthHeader(),
      )
      .pipe(
        tap(() => this.removeFromState(id)),
        catchError((error) => this.handleError(error)),
      );
  }

  updateStatus(id: string, status: CampaignStatus): Observable<Campaign> {
    return this.http
      .patch<Campaign>(
        `${this.UrlServiceV1}${CAMPAIGNS_ENDPOINT}/${id}/status`,
        { status },
        this.obterAuthHeader(),
      )
      .pipe(
        tap((campaign) => this.replaceInState(campaign)),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Uploads the campaign image and returns its readable URL.
   *
   * @remarks
   * The returned URL is a short-lived SAS generated by the backend, not a stable
   * address — re-fetch the campaign rather than persisting it anywhere.
   */
  uploadImage(id: string, file: File): Observable<string> {
    const formData = new FormData();
    formData.append(IMAGE_FORM_FIELD_NAME, file, file.name);

    return this.http
      .post<Campaign>(
        `${this.UrlServiceV1}${CAMPAIGNS_ENDPOINT}/${id}/image`,
        formData,
        this.obterAuthHeaderForFileUpload(),
      )
      .pipe(
        tap((campaign) => this.replaceInState(campaign)),
        map((campaign) => campaign.imageUrl ?? ''),
        catchError((error) => this.handleError(error)),
      );
  }

  private prependToState(campaign: Campaign): void {
    this._campaigns.next([campaign, ...this._campaigns.getValue()]);
  }

  private replaceInState(updated: Campaign): void {
    const campaigns = this._campaigns.getValue().map((campaign) => {
      if (campaign.id === updated.id) {
        return updated;
      }
      return campaign;
    });
    this._campaigns.next(campaigns);
  }

  private removeFromState(id: string): void {
    const remaining = this._campaigns
      .getValue()
      .filter((campaign) => campaign.id !== id);
    this._campaigns.next(remaining);
  }

  /**
   * Rethrows so callers keep their error branches.
   *
   * @remarks
   * Wrapped in an arrow function at every call site on purpose — passing
   * `this.serviceError` by reference loses the instance binding.
   */
  private handleError(error: unknown): Observable<never> {
    return throwError(() => error);
  }
}
